import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Spinner } from "flowbite-react";
import Backend from "../../../Backend/Backend";
import { useNavigate } from "react-router-dom";
import { v4 as uuid } from "uuid";
import { isMobile } from "react-device-detect";
import useDeviceDetection from "../../../Utils/DeviceDetection/DeviceDetection";
import useSound from "use-sound";
import LoginSound from "../../../Sound/login.mp3";
import ErrorSound from "../../../Sound/Error.wav";

const S = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

  .lg {
    min-height: 100vh;
    display: flex; align-items: center; justify-content: center;
    background: var(--background);
    padding: 12px; font-family: 'Inter', sans-serif; box-sizing: border-box;
  }

  .lg-box {
    background: var(--background);
    border: 1px solid var(--hover);
    border-radius: 16px;
    padding: 28px 24px 22px;
    width: 100%; max-width: 380px;
    box-shadow: 0 4px 24px rgba(0,0,0,.08);
  }

  .lg-head { text-align: center; margin-bottom: 22px; }
  .lg-head img { width: 48px; height: 48px; object-fit: contain; margin-bottom: 10px; }
  .lg-head h2 { margin: 0; font-size: 17px; font-weight: 600; color: var(--text); }
  .lg-head p  { margin: 3px 0 0; font-size: 12px; color: var(--subtext); }

  .lg-label {
    display: block; font-size: 11px; font-weight: 500;
    color: var(--subtext); margin-bottom: 5px;
    text-transform: uppercase; letter-spacing: .5px;
  }

  .lg-input {
    width: 100%; height: 42px; padding: 0 12px;
    border: 1px solid var(--hover);
    border-radius: 10px;
    font-size: 13px; color: var(--text);
    background: var(--background);
    outline: none; box-sizing: border-box;
    transition: border-color .18s, background .18s;
    font-family: 'Inter', sans-serif;
  }
  .lg-input:focus {
    border-color: var(--primary);
    background: var(--background);
  }
  .lg-input::placeholder { color: var(--subtext); opacity: .5; }

  .lg-field { margin-bottom: 12px; }

  .lg-btn {
    width: 100%; height: 44px; border: none; border-radius: 10px;
    font-size: 13px; font-weight: 600; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    gap: 6px; transition: opacity .18s;
    font-family: 'Inter', sans-serif; letter-spacing: .2px;
  }
  .lg-btn-primary {
    background: var(--primary);
    color: #fff; margin-top: 14px;
  }
  .lg-btn-primary:hover:not(:disabled) { opacity: .85; }

  .lg-btn-demo {
    background: transparent;
    color: var(--subtext);
    border: 1px solid var(--hover);
    margin-top: 8px;
  }
  .lg-btn-demo:hover:not(:disabled) { border-color: var(--primary); }
  .lg-btn:disabled { opacity: .35; cursor: not-allowed; }

  .lg-divider {
    display: flex; align-items: center; gap: 10px;
    margin: 14px 0 0; color: var(--subtext); font-size: 11px; opacity: .5;
  }
  .lg-divider::before, .lg-divider::after {
    content: ''; flex: 1; height: 1px; background: var(--hover);
  }

  .lg-footer { text-align: center; margin-top: 16px; font-size: 12px; color: var(--subtext); }
  .lg-footer span { color: var(--primary); cursor: pointer; font-weight: 500; }
  .lg-footer span:hover { text-decoration: underline; }

  .lg-links {
    display: flex; justify-content: center; gap: 14px;
    margin-top: 14px;
  }
  .lg-links span {
    font-size: 11px; color: var(--subtext); cursor: pointer; opacity: .6;
  }
  .lg-links span:hover { opacity: 1; text-decoration: underline; }
  .lg-links .sep { opacity: .25; cursor: default; }

  .lg-notice {
    margin-top: 12px; text-align: center;
    font-size: 10px; color: var(--subtext); opacity: .4; letter-spacing: .3px;
  }

  .lg-app-link { text-align: center; margin-top: 10px; font-size: 12px; }
  .lg-app-link a { color: var(--primary); font-weight: 500; text-decoration: none; }
  .lg-app-link a:hover { text-decoration: underline; }

  @media (max-width: 400px) {
    .lg-box { padding: 22px 16px 18px; }
  }
`;

export default function Login() {
  const [backend, setBackend] = useState(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [ip, setIp] = useState("");
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState({ latitude: "", longitude: "", city: "", country: "" });

  const navigate = useNavigate();
  const device = useDeviceDetection();
  const [playLogin] = useSound(LoginSound, { volume: 0.5 });
  const [playError] = useSound(ErrorSound, { volume: 0.5 });

  useEffect(() => {
    const id = "lg-style";
    if (!document.getElementById(id)) {
      const s = document.createElement("style"); s.id = id; s.textContent = S;
      document.head.appendChild(s);
    }
  }, []);

  useEffect(() => {
    (async () => { const b = await new Backend().init(); setBackend(b); getGeoLocation(); })();
  }, []);

  const getGeoLocation = () => {
    if (!navigator.geolocation) { getIpLocation(); return; }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res = await fetch("https://geolocation-db.com/json/");
          const data = await res.json();
          setIp(data.IPv4);
          setLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, city: data.city, country: data.country_name });
        } catch {
          setLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, city: "", country: "" });
        }
      },
      () => getIpLocation(),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const getIpLocation = async () => {
    try {
      const res = await fetch("https://geolocation-db.com/json/");
      const data = await res.json();
      setIp(data.IPv4);
      setLocation({ latitude: data.latitude, longitude: data.longitude, city: data.city, country: data.country_name });
    } catch { }
  };

  const handleLogin = async (loginData) => {
    try {
      setLoading(true);
      const r = await backend.login(loginData);
      setLoading(false);
      if (r.error === "False") {
        saveLoginData(r); toast.success(r.message); await playLogin();
        setTimeout(() => {
          if (r.password === 1) {
            if (r.type === 6) toast.error("Sub Broker Login Coming Soon !!");
            else { navigate("/"); window.location.reload(); }
          } else navigate("/CPassword");
        }, 500);
      } else { toast.error(r.message); playError(); }
    } catch { setLoading(false); toast.error("Something went wrong!"); playError(); }
  };

  const saveLoginData = (r) => {
    localStorage.setItem("userdata", String(r.userdata));
    localStorage.setItem("intraday_sq", String(r.userdata.intraday_sq));
    localStorage.setItem("id", String(r.id));
    localStorage.setItem("server", String(r.server));
    localStorage.setItem("username", String(r.username));
    localStorage.setItem("token", String(r.token));
    localStorage.setItem("user_type", String(r.type));
    localStorage.setItem("sound", true);
    localStorage.setItem("invester", String(r.invester));
    localStorage.setItem("IsLoggedIn", "1");
    const list = JSON.parse(localStorage.getItem("accountsList") || "[]");
    if (!list.find((a) => a.username === r.username)) {
      list.push({ server: r.server, username: r.username, password, user_type: r.type, logo: r.logo || "" });
      localStorage.setItem("accountsList", JSON.stringify(list));
    }
    localStorage.setItem("QuickQty", JSON.stringify([
      { id: "1", qty: "1" }, { id: "2", qty: "2" }, { id: "3", qty: "5" },
      { id: "4", qty: "10" }, { id: "5", qty: "20" }, { id: "6", qty: "30" },
    ]));
  };

  const isLogin = async () => {
    if (!backend) return toast.error("Backend not initialized yet!");
    if (!username) return toast.error("Enter username");
    if (!password) return toast.error("Enter password");
    await handleLogin({
      server: window.SERVER, username, password, device,
      devicename: isMobile ? "Mobile" : "WebBrowser",
      devicetype: isMobile ? "Mobile" : "WebBrowser",
      deviceid: uuid(), deviceip: ip,
      latitude: location.latitude, longitude: location.longitude,
      city: location.city, country: location.country,
    });
  };

  const demo = async () => {
    if (!backend) return toast.error("Backend not initialized yet!");
    try {
      setLoading(true);
      const r = await backend.demo({ server: window.SERVER, });
      console.log(r, "hihih")
      setLoading(false);
      if (r.error === "False") {
        await handleLogin({
          server: r.server, username: r.username, password: r.password, device,
          devicename: isMobile ? "Mobile" : "WebBrowser",
          devicetype: isMobile ? "Mobile" : "WebBrowser",
          deviceid: uuid(), deviceip: ip,
          latitude: location.latitude, longitude: location.longitude,
          city: location.city, country: location.country,
        });
      } else { toast.error(r.message); playError(); }
    } catch (err) { setLoading(false); console.log(err); toast.error("Demo login failed"); playError(); }
  };

  const onKey = (e) => { if (e.key === "Enter") { e.target.blur(); isLogin(); } };

  const showAppLink = window.APP_DOWNLOAD_LINK &&
    ["ATMSTOX", "BOLTTRAD", "APEXBAZAAR", "MONEYMARKET"].includes(window.SERVER);
  const showAndroidLink = window.APP_LINK_ANDROID &&
    ["ARROWTRAD", "SAUDAPRO"].includes(window.SERVER);

  return (
    <div className="lg">
      <div className="lg-box">

        <div className="lg-head">
          <img src={window.LOGO} alt="logo" />
          <h2>Welcome Back</h2>
          <p>Login to continue your trading journey</p>
        </div>

        <div className="lg-field">
          <label className="lg-label">Username</label>
          <input className="lg-input" type="text" placeholder="Enter your username"
            value={username} onChange={(e) => setUsername(e.target.value)} onKeyDown={onKey} />
        </div>

        <div className="lg-field">
          <label className="lg-label">Password</label>
          <input className="lg-input" type="password" placeholder="••••••••"
            value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={onKey} />
        </div>

        <button className="lg-btn lg-btn-primary" disabled={loading} onClick={isLogin}>
          {loading ? <Spinner size="sm" /> : "Login to Account"}
        </button>

        <div className="lg-divider">OR</div>

        <button className="lg-btn lg-btn-demo" disabled={loading} onClick={demo}>
          {loading ? <Spinner size="sm" /> : "Try Demo Account"}
        </button>

        <div className="lg-footer">
          Don't have an account? <span onClick={() => navigate("/register")}>Create Account</span>
        </div>

        {showAppLink && (
          <div className="lg-app-link">
            <a href={window.APP_DOWNLOAD_LINK} target="_blank" rel="noopener noreferrer">Download Mobile App</a>
          </div>
        )}
        {showAndroidLink && (
          <div className="lg-app-link">
            <a href={window.APP_LINK_ANDROID} target="_blank" rel="noopener noreferrer">Download Mobile App</a>
          </div>
        )}

        <div className="lg-links">
          <span onClick={() => navigate("/terms")}>Terms & Conditions</span>
          <span className="sep">|</span>
          <span onClick={() => navigate("/privacy")}>Privacy Policy</span>
        </div>

        <div className="lg-notice">THIS APPLICATION IS FOR TRAINING PURPOSE ONLY.</div>
      </div>
    </div>
  );
}
